/**
 * The icon atlas is a single raster image with all of the variants of aircraft bodies,
 * rotors (for helicopters), and shadows necessary to render efficiently on the GPU.
 *
 * The atlas image is loaded directly onto the GPU and offsets within it looked up for rendering.
 */
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { AircraftIcons, AircraftIconInfo } from './Icons';
import { AircraftIconVariant } from './types';
import {
  DEFAULT_STROKE_COLOR,
  STROKE_LINE_WIDTH,
  fillColorForVariant,
} from './AircraftsLayer.styles';
// Written by `scripts/generate-animated-aircraft-pngs.js`.
import aircraftRotors from './aircraftRotors.json';

export const AIRCRAFT_ICON_VARIANTS: readonly AircraftIconVariant[] = [
  'default',
  'active',
  'ground',
  'activeground',
];

const ATLAS_CELL_PX = 148;
// WebGL2 spec requires minimum 2048x2048 pixel textures, so if this number needs to increase,
// be mindful of trying to keep within the limits. In practice the number is higher.
const ATLAS_COLUMNS = 10;

// Bigger value means a more spread out shadow. For performance shadow blur is rendered into the atlas.
const SHADOW_BLUR_ATLAS_PX = 5;

export type AircraftIconAtlasEntry = {
  x: number;
  y: number;
  width: number;
  height: number;
  anchorX: number;
  anchorY: number;
  mask?: boolean;
};

export type AircraftRotorInfo = {
  /** X coordinate of the hub (rotor center) in the icon's sprite. */
  hubX?: number;
  /** Y coordinate of the hub in the icon's sprite. */
  hubY: number;
  /** 1 or -1, dictates spin direction. */
  direction: number;
};

export type AircraftRotorManifestEntry = {
  /** How long one full revolution takes. */
  animationDurationMs: number;
  rotors: AircraftRotorInfo[];
};

export type AircraftRotorManifest = Record<string, AircraftRotorManifestEntry>;

const ROTOR_MANIFEST: AircraftRotorManifest = aircraftRotors;

export type AircraftIconAtlas = {
  /** PNG data URL of the packed atlas. */
  atlasUrl: string;
  mapping: Record<string, AircraftIconAtlasEntry>;
  rotors: AircraftRotorManifest;
};

/** Key for the full-colour, complete icon in the atlas mapping. */
export const aircraftIconKey = (
  name: string,
  variant: AircraftIconVariant,
): string => `${name}-${variant}`;

/** Key for the shadow silhouette. */
export const aircraftShadowIconKey = (
  name: string,
  variant: AircraftIconVariant,
): string => `${name}-${variant}-shadow`;

/** Key for a helicopter's main body (no rotors). */
export const aircraftBodyIconKey = (
  name: string,
  variant: AircraftIconVariant,
): string => `${name}-${variant}-body`;

/** Key for a helicopter's main body shadow (no rotors). */
export const aircraftBodyShadowIconKey = (
  name: string,
  variant: AircraftIconVariant,
): string => `${aircraftBodyIconKey(name, variant)}-shadow`;

/** Key for one rotor. */
export const aircraftRotorIconKey = (
  name: string,
  variant: AircraftIconVariant,
  rotorIndex: number,
): string => `${name}-${variant}-rotor${rotorIndex}`;

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = (): void => {
      resolve(image);
    };
    image.onerror = (): void => {
      reject(new Error(`Failed to load aircraft icon: ${src}`));
    };
    image.src = src;
  });
};

/** Serializes one of the inline SVG icons to a data URL, colored based on `variant`. */
const svgIconToDataUrl = (
  icon: AircraftIconInfo,
  variant: AircraftIconVariant,
): string => {
  const markup = ReactDOMServer.renderToStaticMarkup(
    React.cloneElement(icon.svg!, {
      width: ATLAS_CELL_PX,
      height: ATLAS_CELL_PX,
      fill: fillColorForVariant(variant),
      stroke: DEFAULT_STROKE_COLOR,
      strokeWidth: STROKE_LINE_WIDTH,
    }),
  );
  return `data:image/svg+xml;base64,${window.btoa(markup)}`;
};

const iconVariantSource = (
  icon: AircraftIconInfo,
  variant: AircraftIconVariant,
): string => {
  if (icon.svg) {
    return svgIconToDataUrl(icon, variant);
  }
  if (icon.urls) {
    return icon.urls[variant];
  }
  throw new Error(`Missing icon source for ${icon.name}-${variant}`);
};

/**
 * Returns the static URL for an aircraft body or rotor for the given `variant`.
 */
const spriteUrl = (
  icon: AircraftIconInfo,
  variant: AircraftIconVariant,
  suffix: string,
): string => {
  const url = icon.urls?.[variant];
  if (!url) {
    throw new Error(`No animated icon to derive ${suffix} from: ${icon.name}`);
  }
  // Rotors (the `suffix`) sit next to the main body PNG
  return url.replace(/\.png$/, `-${suffix}.png`);
};

/** One packed cell: what to draw, under what key, anchored where. */
type AtlasCell = {
  key: string;
  src: string;
  // Defaults to the icon center (0, 0). Rotors have an offset so they spin in place.
  anchorX?: number;
  anchorY?: number;
  // If true, this is a blurry shadow cell.
  isShadow?: boolean;
};

const shadowCell = (cell: AtlasCell): AtlasCell => ({
  key: `${cell.key}-shadow`,
  src: cell.src,
  isShadow: true,
});

/**
 * Returns all of the atlas's cell metadata, including both the static body icons, any animated rotors,
 * and all variants (colors, shadows).
 */
const buildCellList = (rotors: AircraftRotorManifest): AtlasCell[] =>
  AircraftIcons.flatMap((icon) =>
    AIRCRAFT_ICON_VARIANTS.flatMap((variant) => {
      // The whole-aircraft cell doubles as the body for icons with no moving
      // parts. Helicopters override that with the body cell below.
      const whole: AtlasCell = {
        key: aircraftIconKey(icon.name, variant),
        src: iconVariantSource(icon, variant),
      };

      const rotorInfo = rotors[icon.name]?.rotors;
      if (!rotorInfo?.length) {
        // Fixed-wing
        return [whole, shadowCell(whole)];
      }

      // Helicopters cast their shadow from the body (no rotors).
      const body: AtlasCell = {
        key: aircraftBodyIconKey(icon.name, variant),
        src: spriteUrl(icon, variant, 'body'),
      };
      return [
        whole,
        body,
        shadowCell(body),
        ...rotorInfo.map((rotor, index) => ({
          key: aircraftRotorIconKey(icon.name, variant, index),
          src: spriteUrl(icon, variant, `rotor${index}`),
          anchorX: rotor.hubX,
          anchorY: rotor.hubY,
        })),
      ];
    }),
  );

/**
 * Packs every aircraft sprite into a single texture (think image) that a Deck.GL IconLayer can use
 * by addressing into it at specific offsets.
 */
export const buildAircraftIconAtlas = async (): Promise<AircraftIconAtlas> => {
  const cells = buildCellList(ROTOR_MANIFEST);

  // A shadow cell reuses its base cell's source, so load by unique URL rather
  // than per cell.
  const sources = [...new Set(cells.map((cell) => cell.src))];
  const loaded = await Promise.all(sources.map(loadImage));
  const imagesBySource = new Map(
    sources.map((src, index) => [src, loaded[index]]),
  );

  const canvas = document.createElement('canvas');
  canvas.width = ATLAS_COLUMNS * ATLAS_CELL_PX;
  canvas.height = Math.ceil(cells.length / ATLAS_COLUMNS) * ATLAS_CELL_PX;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Could not get a 2d context to build the aircraft atlas');
  }

  const mapping: Record<string, AircraftIconAtlasEntry> = {};

  cells.forEach((cell, index) => {
    const x = (index % ATLAS_COLUMNS) * ATLAS_CELL_PX;
    const y = Math.floor(index / ATLAS_COLUMNS) * ATLAS_CELL_PX;
    const image = imagesBySource.get(cell.src);
    if (!image) {
      throw new Error(`No loaded image for atlas cell: ${cell.key}`);
    }

    if (cell.isShadow) {
      context.save();
      // Cells are packed edge to edge, so without a clip the blur would smear
      // into whichever icons happen to sit next to this one.
      context.beginPath();
      context.rect(x, y, ATLAS_CELL_PX, ATLAS_CELL_PX);
      context.clip();
      context.filter = `blur(${SHADOW_BLUR_ATLAS_PX}px)`;
      context.drawImage(image, x, y, ATLAS_CELL_PX, ATLAS_CELL_PX);
      context.restore();
    } else {
      context.drawImage(image, x, y, ATLAS_CELL_PX, ATLAS_CELL_PX);
    }

    mapping[cell.key] = {
      x,
      y,
      width: ATLAS_CELL_PX,
      height: ATLAS_CELL_PX,
      anchorX: cell.anchorX ?? ATLAS_CELL_PX / 2,
      anchorY: cell.anchorY ?? ATLAS_CELL_PX / 2,
      // `mask` makes IconLayer keep only the alpha and tint it with getColor,
      // which is what turns the blurred silhouette into a soft shadow with
      // whatever color we want.
      ...(cell.isShadow ? { mask: true } : {}),
    };
  });

  return {
    atlasUrl: canvas.toDataURL('image/png'),
    mapping,
    rotors: ROTOR_MANIFEST,
  };
};
